import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ButtonGroup, Button, Modal, Form, Image } from 'react-bootstrap';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faTrash, faPlus, faAngleRight, faAngleLeft } from '@fortawesome/free-solid-svg-icons';

import { useLanguage } from '../contexts/LanguageContext';
import { useMode } from '../contexts/ModeContext';
import { useResto } from '../contexts/RestoContext';
import { setFlashMessage, useWebsite } from '../contexts/WebsiteContext';
import { useAuth } from '../contexts/AuthContext';
import config from '../config/config';

const Menus = () => {
  const { t } = useTranslation();
  const { activeLanguage } = useLanguage();
  const { restoData } = useResto();
  const { isModeAdmin } = useMode();
  const { setIsLoading } = useWebsite();
  const { token } = useAuth();

  const availableLanguages = restoData.availableLanguages;
  const { apiUrl } = config;
  const apiPath = `${apiUrl}menu`;

  const [menusByLanguage, setMenusByLanguage] = useState({});
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    id: '',
    language: '',
    label: '',
    file: null
  });
  const [isAdding, setIsAdding] = useState(false);

  useEffect(() => {
    fetchItems();
  }, []);

  const fetchItems = async () => {
    try {
      const response = await axios.get(`${apiPath}?website=${restoData.website}`);
      const menus = response.data;
      const menusByLang = {};
      // Group menus by language
      menus.forEach(menu => {
        if (!menusByLang[menu.language]) {
          menusByLang[menu.language] = [];
        }
        menusByLang[menu.language].push(menu);
      });
      setMenusByLanguage(menusByLang);
    } catch (error) {
      setFlashMessage(t('ERROR_MESSAGE'), 'error');
    }
  };

  const handleTrashClick = async (menuId) => {
    setIsLoading(true);
    try {
      await axios.delete(
           `${apiPath}/${menuId}`,
           {
               headers: {
                   Authorization: `Bearer ${token}`
               }
           }
      );
      fetchItems();
      setFlashMessage(t('SUCCESS_DELETED_MESSAGE'), 'success');
    } catch (error) {
      setFlashMessage(t('ERROR_MESSAGE'), 'error');
      console.error('Error deleting item:', error);
    }
  };

  const handleEditFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevFormData => ({
      ...prevFormData,
      [name]: value
    }));
  };
  
  const handleEditFileChange = (e) => {
    setFormData(prevFormData => ({
      ...prevFormData,
      file: e.target.files[0]
    }));
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const response = await axios.post(`${apiPath}/${formData.id}`, 
      formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      handleCloseEditModal();
      fetchItems();
      setFlashMessage(t('SUCCESS_CREATED_MESSAGE'), 'success');
    } catch (error) {
      setFlashMessage(t('ERROR_MESSAGE'), 'error');
    }
    setIsLoading(false);
  };

  const handleRankingUpdate = async (direction, itemId) => {
    try {
      setIsLoading(true);
      await axios.put(
          `${apiPath}/${itemId}`,
          { ranking: direction }, {
              headers: {
                  Authorization: `Bearer ${token}`
              }
          }
      );
      fetchItems();
      setFlashMessage(t('SUCCESS_UPDATED_MESSAGE'), 'success');
    } catch (error) {
      setFlashMessage(t('ERROR_MESSAGE'), 'error');
      console.error(`Error updating item ranking (${direction}):`, error);
    }
    setIsLoading(false);
  };

  const handleEditModalClose = () => {
    setEditModalOpen(false);
    setIsAdding(false);
    setFormData({
      id: '',
      language: '',
      label: '',
      file: null
    });
  };

  const handleEditModalOpen = (menu) => {
    setFormData({
      id: menu.id,
      language: menu.language,
      label: menu.label,
      file: null
    });
    setIsAdding(false);
    setEditModalOpen(true);
  };

  const handleAddSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const formDataToSend = new FormData();
      formDataToSend.append('language', formData.language);
      formDataToSend.append('label', formData.label);
      formDataToSend.append('file', formData.file);
      formDataToSend.append('website', restoData.website);

      const response = await axios.post(apiPath, formDataToSend, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      handleCloseEditModal();
      fetchItems();
      setFlashMessage(t('SUCCESS_CREATED_MESSAGE'), 'success');
    } catch (error) {
      setFlashMessage(t('ERROR_MESSAGE'), 'error');
    }
    setIsLoading(false);
  };

  const handleAddFormChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleAddFileChange = (e) => {
    setFormData({
      ...formData,
      file: e.target.files[0]
    });
  };

  const handleCloseEditModal = () => {
    setFormData({
        id: '',
        language: '',
        label: '',
        file: null
    });
    setEditModalOpen(false);
  };

  return (
    <>
      {isModeAdmin &&
        <>
          {availableLanguages.map(language => (
            <div key={language} className="text-center background-main-color-80" style={{ color: 'rgb(255,255,255)', padding: '10px 0', marginBottom:'10px'}}>
              <div style={{ borderBottom:'solid 1px rgba(255,255,255,0.2)', margin:'0 10px 10px 10px'}}>
                Carte(s) <Image src={`${config.kubuniImagesUrl}general-icons/flag_${language}_icon_24.png`} className="flag" alt={`${language}`} />
              </div>  
              <div>
                {menusByLanguage[language] && menusByLanguage[language].map((menu, index) => (
                  <ButtonGroup key={menu.id} style={{ marginLeft:'5px'}}>
                    <Button variant="primary">
                      <a href={`${config.documentsUrl}menus/${menu.filename}`} target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'none' }}>
                        {menu.label}
                      </a>
                    </Button>
                    {!(index === 0) && (<Button variant="primary"><FontAwesomeIcon icon={faAngleLeft} onClick={() => handleRankingUpdate('up', menu.id)}/></Button>)}
                    {!(index === menusByLanguage[language].length - 1) && (<Button variant="primary"><FontAwesomeIcon icon={faAngleRight} onClick={() => handleRankingUpdate('down', menu.id)} /></Button>)}
                    <Button variant="primary" onClick={() => handleEditModalOpen(menu)}><FontAwesomeIcon icon={faEdit} /></Button>
                    <Button variant="primary" onClick={() => handleTrashClick(menu.id)}><FontAwesomeIcon icon={faTrash} /></Button>
                  </ButtonGroup>
                ))}
                <Button variant="primary" style={{margin: '0 5px'}}>
                  <FontAwesomeIcon icon={faPlus} onClick={() => { setIsAdding(true); setEditModalOpen(true); }} />
                </Button>
              </div>
            </div>
          ))}

          <Modal show={editModalOpen} onHide={handleEditModalClose}>
            <Modal.Header closeButton>
                <Modal.Title>{ isAdding ? t('ADD') : t('EDIT') }</Modal.Title>
            </Modal.Header>
            <Form onSubmit={isAdding ? handleAddSubmit : handleEditSubmit}>
              <Modal.Body>
                  <Form.Group>
                    <Form.Label>{ t('LANGUAGE') }</Form.Label>
                    <div>
                      {availableLanguages.map(language => (
                        <label key={language} style={{ marginRight: '10px' }}>
                          <input 
                            type="radio"
                            name="language"
                            value={language}
                            onChange={isAdding ? handleAddFormChange : handleEditFormChange}
                            checked={formData.language === language}
                            style={{ display: 'none' }}
                          />
                          <Image 
                            src={`${config.kubuniImagesUrl}general-icons/flag_${language}_icon_24.png`} 
                            alt={`${language}`}
                            style={{ boxShadow: formData.language === language ? '0 0 5px 2px rgba(0, 0, 255, 0.75)' : 'none' }}
                          />
                        </label>
                      ))}
                    </div>
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>{ t('LABEL') }</Form.Label>
                    <Form.Control 
                      type="text" 
                      name="label" 
                      value={formData.label} 
                      onChange={isAdding ? handleAddFormChange : handleEditFormChange} 
                      required 
                    />
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>{ t('PDF') }</Form.Label>
                    <Form.Control 
                      type="file" 
                      id="file" 
                      name="file" 
                      onChange={isAdding ? handleAddFileChange : handleEditFileChange} 
                      accept=".pdf" 
                      required 
                    />
                  </Form.Group>
              </Modal.Body>
              <Modal.Footer className="justify-content-center">
                <Button variant="secondary" onClick={handleEditModalClose}>
                    { t('CANCEL') }
                </Button>
                <Button variant="primary" type="submit">
                    { t('CONFIRM') }
                </Button>
              </Modal.Footer>
            </Form>
          </Modal>
        </>
      }   

      {!isModeAdmin &&
        <div className="d-flex justify-content-center background-main-color-80" style={{ color: 'rgb(255,255,255)', padding: '10px 0'}}>
          {menusByLanguage[activeLanguage] && menusByLanguage[activeLanguage].map(menu => (
            <a key={menu.id} href={`${config.documentsUrl}menus/${menu.filename}`} target="_blank" rel="noopener noreferrer" style={{margin: '0 5px'}}>
              <Button variant="primary">
                {menu.label}
              </Button>
            </a>
          ))}
        </div>
      }
    </>
  );
}

export default Menus;
